/**
 * Janus for someone who reads pictures, not spreadsheets (owner, 2026-10-17).
 *
 * What a salesperson needs about each ship-to: how many of the Janus SKUs it
 * carries, when it first shipped, and whether it has reordered (every Janus
 * date is a ship date -- owner, 2026-10-19). So the
 * section leads with tiles, a map, a card per ship-to and a timeline of its
 * orders; the per-SKU tables stay underneath as detail. With no rep chosen it
 * is the manager's roll-up: every ship-to, plus each rep's ship-tos by where
 * they stand.
 */

import { lazy, Suspense, useState } from 'react'

import type { EChartsOption } from 'echarts'

import { Card, Empty } from '../shared/Card'
import { type Keyed, text } from '../shared/rows'
import type { MapPoint, OrderDot } from './JanusCharts'
import {
  amount,
  GRID,
  LEGEND_TOP,
  num,
  PULL_THROUGH,
  SHIP_TO_STATUS,
  statusColour,
  STOCK_UP_NOTE,
  STOCK_UP_ONLY,
  type Unit,
} from './format'
import { ChartFrame, Kpi } from './parts'

const JanusMap = lazy(() => import('./JanusCharts').then((m) => ({ default: m.JanusMap })))
const OrderTimeline = lazy(() => import('./JanusCharts').then((m) => ({ default: m.OrderTimeline })))

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "27 Aug" from an ISO date, without the viewer's time zone moving the day. */
function day(iso: string | null): string {
  if (iso === null) return '—'
  const [, month, date] = iso.split('-')
  return `${String(Number(date))} ${MONTHS[Number(month) - 1] ?? ''}`
}

/** "Mojito Mix" from "5227 - SO Mojito Mix FZN 12pk 30.5oz". */
function productName(item: string | null): string {
  if (item === null) return ''
  return item
    .replace(/^\S+\s+-\s+/, '')
    .replace(/^(SOI|SO)\s+/, '')
    .replace(/\s+(FZN|Gln|HGln|Qt)\b.*$/i, '')
}

/** How many ship-tos stand where, per rep: the manager's picture. */
function RepStanding({ shipTos }: { shipTos: readonly Keyed[] }) {
  const reps = [...new Set(shipTos.map((row) => text(row['sales_rep']) ?? ''))]
  const count = (rep: string, status: string) =>
    shipTos.filter((row) => row['sales_rep'] === rep && row['status'] === status).length
  const order = [...reps].sort(
    (a, b) =>
      shipTos.filter((row) => row['sales_rep'] === a).length -
      shipTos.filter((row) => row['sales_rep'] === b).length,
  )
  const option: EChartsOption = {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    legend: { ...LEGEND_TOP, data: SHIP_TO_STATUS.map((s) => s.key) },
    grid: GRID,
    xAxis: { type: 'value', minInterval: 1 },
    yAxis: { type: 'category', data: order, axisTick: { show: false } },
    series: SHIP_TO_STATUS.map((status) => ({
      name: status.key,
      type: 'bar' as const,
      stack: 'ship-tos',
      data: order.map((rep) => count(rep, status.key)),
      itemStyle: { color: status.colour },
      barWidth: 16,
    })),
  }
  return (
    <div className="srp-chart">
      <p className="srp-chart-title">Ship-tos by rep, and where they stand</p>
      <ChartFrame option={option} label="Janus ship-tos by rep and status" height={Math.max(180, order.length * 32 + 70)} />
    </div>
  )
}

function SkuDots({ row, skuList }: { row: Keyed; skuList: readonly Keyed[] }) {
  const carried = new Set((text(row['sku_list']) ?? '').split(',').filter(Boolean))
  const reordered = new Set((text(row['reordered_list']) ?? '').split(',').filter(Boolean))
  return (
    <ul className="srp-sku-dots" aria-label="Janus SKUs carried">
      {skuList.map((sku) => {
        const code = text(sku['sku']) ?? ''
        const name = productName(text(sku['item']))
        const state = reordered.has(code) ? 'reordered' : carried.has(code) ? 'carried' : 'none'
        const label =
          state === 'reordered' ? 'carried and reordered' : state === 'carried' ? 'carried' : 'not carried'
        return (
          <li key={code} className={`srp-sku-dot is-${state}`} title={`${code} ${name}: ${label}`}>
            <span className="srp-sr">{`${code} ${name}: ${label}`}</span>
          </li>
        )
      })}
    </ul>
  )
}

function ShipToCards({
  rows,
  skuList,
  showRep,
}: {
  rows: readonly Keyed[]
  skuList: readonly Keyed[]
  showRep: boolean
}) {
  return (
    <ul className="srp-cards">
      {rows.map((row) => {
        const status = text(row['status']) ?? ''
        const skus = num(row['skus']) ?? 0
        const reorder = num(row['reorder_cases']) ?? 0
        return (
          <li
            key={`${text(row['sales_rep']) ?? ''}-${text(row['ship_to_number']) ?? ''}`}
            className="srp-card"
            style={{ borderTopColor: statusColour(status) }}
          >
            <p className="srp-card-name">{text(row['ship_to_customer']) ?? text(row['ship_to_number'])}</p>
            <p className="srp-card-sub">
              {text(row['end_user'])}
              {showRep ? ` · ${text(row['sales_rep']) ?? ''}` : ''}
            </p>
            <p className="srp-card-skus">
              <strong>{skus}</strong> of {skuList.length} Janus SKUs
            </p>
            <SkuDots row={row} skuList={skuList} />
            <dl className="srp-card-facts">
              <div>
                <dt>First shipped</dt>
                <dd>{day(text(row['first_order_date']))}</dd>
              </div>
              <div>
                <dt>Opening</dt>
                <dd>{amount(num(row['opening_cases']), 'cases')} cs</dd>
              </div>
              <div>
                <dt>Reorders</dt>
                <dd>{reorder > 0 ? `${amount(reorder, 'cases')} cs` : 'none yet'}</dd>
              </div>
              {num(row['days_to_first_reorder']) !== null && (
                <div>
                  <dt>First reorder</dt>
                  <dd>{`${String(num(row['days_to_first_reorder']))} days in`}</dd>
                </div>
              )}
            </dl>
            {text(row['reorder_signal']) === STOCK_UP_ONLY && (
              // Still a reorder, flagged (owner, 2026-11-04): every follow-on so far
              // was the same cases on 5+ SKUs -- stocking up, not yet sell-through.
              <p className="srp-card-stockup">{STOCK_UP_NOTE}</p>
            )}
            {(num(row['booked_cases']) ?? 0) > 0 && (
              // Booked and not yet shipped (owner, 2026-10-19): a reorder or new
              // SKUs on order, and when the first of it is due to ship.
              <p className="srp-card-booked">
                On order: {amount(num(row['booked_cases']), 'cases')} cs, ships{' '}
                {day(text(row['booked_ship_date']))}
              </p>
            )}
            <p className="srp-card-status" style={{ color: statusColour(status) }}>
              {status}
            </p>
          </li>
        )
      })}
    </ul>
  )
}

/** Cards shown before "Show all": enough to scroll past on a laptop. */
const CARD_LIMIT = 24

/**
 * Taking the line vs. trying it (owner, 2026-10-18, raised from four to five
 * the same day): a ship-to with five or more Janus SKUs is taking more of the
 * line; one with one to four may be a
 * customer whose existing items now sit inside the Janus program. The count
 * is of Janus-business SKUs only -- a pair that already sold Jan-Jul 2026 is
 * base business and not counted -- so it is the same number as on the card.
 */
export const LINE_THRESHOLD = 5

type Breadth = 'all' | 'line' | 'trying'

const BREADTH: readonly { key: Breadth; label: string }[] = [
  { key: 'all', label: 'All ship-tos' },
  { key: 'line', label: `${String(LINE_THRESHOLD)} or more SKUs` },
  { key: 'trying', label: `1 to ${String(LINE_THRESHOLD - 1)} SKUs` },
]

function keeps(breadth: Breadth, row: Keyed): boolean {
  const skus = num(row['skus']) ?? 0
  if (breadth === 'line') return skus >= LINE_THRESHOLD
  if (breadth === 'trying') return skus < LINE_THRESHOLD
  return true
}

export function JanusPictures({
  shipTos,
  orderDays,
  skuList,
  chosen,
  unit,
}: {
  shipTos: readonly Keyed[]
  orderDays: readonly Keyed[]
  skuList: readonly Keyed[]
  chosen: string | null
  unit: Unit
}) {
  const [breadth, setBreadth] = useState<Breadth>('all')
  const ofRep = chosen === null ? shipTos : shipTos.filter((row) => row['sales_rep'] === chosen)
  const rows = ofRep.filter((row) => keeps(breadth, row))
  const kept = new Set(rows.map((row) => `${text(row['sales_rep']) ?? ''}|${text(row['ship_to_number']) ?? ''}`))
  const days = orderDays.filter((row) =>
    kept.has(`${text(row['sales_rep']) ?? ''}|${text(row['ship_to_number']) ?? ''}`),
  )
  const counts = Object.fromEntries(
    BREADTH.map((b) => [b.key, ofRep.filter((row) => keeps(b.key, row)).length]),
  ) as Record<Breadth, number>
  const sum = (key: string) => rows.reduce((total, row) => total + (num(row[key]) ?? 0), 0)
  const placements = sum('skus')
  const reordering = rows.filter((row) => (num(row['skus_reordered']) ?? 0) > 0).length
  const pullingThrough = rows.filter((row) => row['reorder_signal'] === PULL_THROUGH).length
  const overdue = rows.filter((row) => String(row['status'] ?? '').includes('overdue')).length

  const points: MapPoint[] = rows.flatMap((row) => {
    const lat = num(row['latitude'])
    const lon = num(row['longitude'])
    if (lat === null || lon === null) return []
    return [
      {
        name: text(row['ship_to_customer']) ?? text(row['ship_to_number']) ?? '',
        detail: `${String(num(row['skus']) ?? 0)} SKUs · ${amount(num(row['total_cases']), 'cases')} cases · first shipped ${day(text(row['first_order_date']))}`,
        status: text(row['status']) ?? '',
        cases: num(row['total_cases']) ?? 0,
        lat,
        lon,
      },
    ]
  })
  const byCases = [...rows].sort((a, b) => (num(b['total_cases']) ?? 0) - (num(a['total_cases']) ?? 0))
  const names = byCases.map((row) => text(row['ship_to_customer']) ?? text(row['ship_to_number']) ?? '')
  const dots: OrderDot[] = days.map((row) => ({
    shipTo: text(row['ship_to_customer']) ?? text(row['ship_to_number']) ?? '',
    date: text(row['order_date']) ?? '',
    cases: num(row['cases']) ?? 0,
    kind: text(row['kind']) ?? '',
    skus: num(row['skus']) ?? 0,
    signal: text(row['reorder_signal']),
  }))
  const unmapped = rows.length - points.length

  return (
    <Card
      title={chosen === null ? 'Janus: bar mixer launch' : `Janus: ${chosen}`}
      subtitle="Launched 1 Aug 2026, reported with no budget. A ship-to that already bought a Janus SKU January to July 2026 counts as base business for that SKU and is in the comparison above."
    >
      {ofRep.length > 0 && (
        <div className="srp-breadth" role="group" aria-label="How much of the line">
          {BREADTH.map((b) => (
            <button
              key={b.key}
              type="button"
              className={`srp-breadth-option${breadth === b.key ? ' is-on' : ''}`}
              aria-pressed={breadth === b.key}
              onClick={() => {
                setBreadth(b.key)
              }}
            >
              {b.label} <span className="srp-breadth-count">{counts[b.key]}</span>
            </button>
          ))}
        </div>
      )}
      {rows.length === 0 ? (
        <Empty
          label={
            ofRep.length > 0
              ? 'No ship-tos match this filter.'
              : chosen === null
                ? 'No Janus business yet.'
                : `${chosen} has no Janus business yet.`
          }
        />
      ) : (
        <>
          <section className="srp-kpis" aria-label="Janus at a glance">
            <Kpi label="Ship-tos ordering" value={amount(rows.length, 'cases')} />
            <Kpi
              label="SKU placements"
              value={amount(placements, 'cases')}
              sub={`${(placements / rows.length).toFixed(1)} SKUs per ship-to`}
            />
            <Kpi
              label="Ship-tos reordering"
              value={amount(reordering, 'cases')}
              sub={`${String(Math.round((reordering / rows.length) * 100))}% of ship-tos · ${String(pullingThrough)} pulling through`}
            />
            <Kpi
              label="Cases since launch"
              value={amount(sum('total_cases'), 'cases')}
              sub={`${amount(sum('opening_cases'), 'cases')} opening · ${amount(sum('reorder_cases'), 'cases')} reorders`}
            />
            <Kpi
              label="Overdue to reorder"
              value={amount(overdue, 'cases')}
              toneValue={overdue > 0 ? -1 : undefined}
              sub={overdue > 0 ? 'ship-tos to call' : 'none'}
            />
          </section>

          <div className="srp-charts">
            <div className="srp-chart">
              <p className="srp-chart-title">Where Janus is</p>
              <Suspense fallback={<div className="state">Loading map…</div>}>
                <JanusMap points={points} />
              </Suspense>
              {unmapped > 0 && (
                <p className="srp-hint">
                  {unmapped} ship-to{unmapped === 1 ? ' has' : 's have'} no location and {unmapped === 1 ? 'is' : 'are'} not on the map.
                </p>
              )}
            </div>
            {chosen === null ? (
              <RepStanding shipTos={rows} />
            ) : (
              <div className="srp-chart">
                <p className="srp-chart-title">Shipments since launch</p>
                <Suspense fallback={<div className="state">Loading chart…</div>}>
                  <OrderTimeline dots={dots} shipTos={names.slice(0, 20)} />
                </Suspense>
              </div>
            )}
          </div>

          <h3 className="srp-section-title">
            {chosen === null ? 'Every ship-to' : 'Ship-tos'} ({rows.length}), largest first
          </h3>
          <p className="srp-legend">
            {SHIP_TO_STATUS.map((s) => (
              <span key={s.key}>
                <span className="srp-swatch" style={{ background: s.colour }} />
                {s.key}
              </span>
            ))}
            <span>
              <span className="srp-sku-dot is-reordered srp-inline" /> SKU reordered
            </span>
            <span>
              <span className="srp-sku-dot is-carried srp-inline" /> SKU carried
            </span>
            <span>
              <span className="srp-sku-dot is-none srp-inline" /> not carried
            </span>
          </p>
          <ShipToCards rows={byCases.slice(0, CARD_LIMIT)} skuList={skuList} showRep={chosen === null} />
          {byCases.length > CARD_LIMIT && (
            <p className="srp-hint">
              The {byCases.length - CARD_LIMIT} smaller ship-tos are in the detail at the foot of the page.
            </p>
          )}
          {chosen === null && (
            <>
              <h3 className="srp-section-title">Shipments since launch, largest 20 ship-tos</h3>
              <Suspense fallback={<div className="state">Loading chart…</div>}>
                <OrderTimeline dots={dots} shipTos={names.slice(0, 20)} />
              </Suspense>
            </>
          )}
          <p className="srp-hint">
            {unit === 'fob' ? 'Janus figures are in cases. ' : ''}Dated by ship date: only shipped
            orders count. A ship-to&apos;s first shipment of each SKU opens it; every later
            shipment of that SKU there is a reorder. &ldquo;Overdue&rdquo; means a month with no
            reorder after opening, or a week past its usual reorder gap; a reorder booked and not yet
            shipped reads &ldquo;Reorder booked&rdquo; instead.
          </p>
        </>
      )}
    </Card>
  )
}
